
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import { UserT } from '@/core/types/users.types'
import { GatewayRoleT } from '@/core/types/gateway.types'
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'
import { users } from '@/core/realisations/users.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { required, email, requiredIf } from '@vuelidate/validators'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<UserT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cabinet-update', 'delete', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            id: null as number | null,
            name: null as string | null,
            email: null as string | null,
            phone: null as string | null,
            password: null as string | null,
            roles: [] as number[],
            uuid_1c: null as string | null,
            company_id: null as number | null,
            is_advanced: true,
        })

        const user_state = reactive({
            is_loading: false,
        })

        const data_state = reactive({
            roles: new DataState<GatewayRoleT>({}),
            companies: new DataState<CompanyT>({}),
        })

        let state_form$ = useVuelidate(
            {
                name: { required },
                email: { required, email },
                phone: { required },
                password: {
                    required: requiredIf(function () {
                        if (props.isCreate) return true
                        return false
                    }),
                },
                roles: { required },
                company_id: { required },
            },
            state
        )

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление пользователя'
            if (props.isUpdate) return 'Редактирование пользователя'
            if (props.isDelete) return 'Удаление пользователя'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        // roles

        const loadRoles = async (page: number, name?: string) => {
            data_state.roles.pagination.current_page = page

            try {
                data_state.roles.is_loading = true

                const response = await gateway.getRoles(page, { name, isExtended: 0 })
                data_state.roles.list = response.data

                data_state.roles.pagination.total_pages = response.meta.last_page

                data_state.roles.is_failed = false
                data_state.roles.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.roles.failure.catchError(error)

                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_failed = true
                }
            } finally {
                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_loading = false
                }
            }
        }

        const loadMoreRoles = async (page: number) => {
            if (page > data_state.roles.pagination.total_pages) return
            data_state.roles.pagination.current_page = page

            try {
                data_state.roles.is_loading = true

                const response = await gateway.getRoles(page, { isExtended: 0 })
                data_state.roles.list = data_state.roles.list.concat(response.data)

                data_state.roles.pagination.total_pages = response.meta.last_page

                data_state.roles.is_failed = false
                data_state.roles.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.roles.failure.catchError(error)

                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_failed = true
                }
            } finally {
                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_loading = false
                }
            }
        }

        const searchRoles = (name: string) => {
            loadRoles(1, name)
        }

        const unshiftFindedRole = async (role: GatewayRoleT) => {
            await loadRoles(data_state.roles.pagination.current_page)
            data_state.roles.list.unshift(role)
        }

        // companies

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        const loadUser = async () => {
            try {
                user_state.is_loading = true

                const response = await users.getUserFromCabinet(props.initialValue.id)
                state.uuid_1c = response.data.uuid_1c
                state.is_advanced = response.data.is_advanced
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных пользователя произошла ошибка',
                })
            } finally {
                user_state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadRoles(1)
            loadCompanies(1)
            if (!props.initialValue) return

            loadUser()

            state.id = props.initialValue.id
            state.name = props.initialValue.name
            state.email = props.initialValue.email
            state.phone = props.initialValue.phone
            state.roles = props.initialValue.roles.map((role) => role.id)
            state.company_id = props.initialValue.company_id
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', toRaw(state))
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', { id: state.id, payload: toRaw(state) })
        }

        const emitDelete = () => {
            emit('delete', state.id)
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        const onAdditionalAction = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('cabinet-update', { id: state.id, payload: toRaw(state) })
        }

        return {
            state,
            user_state,
            data_state,
            state_form$,
            modalTitle,
            modalConfirmButtonText,
            emitCancel,
            emitManager,
            //
            loadRoles,
            loadMoreRoles,
            searchRoles,
            unshiftFindedRole,
            //
            onAdditionalAction,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
        }
    },
})
